import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { addLoyaltyPoints } from '@/lib/loyalty';

// ============================================================
// Fidélité & Récompenses — programme de points du marchand.
// Réglages persistés dans `loyalty_settings` ; points et historique
// lus depuis `pointfidelite` / `pointtransactions`. Isolé par commerce.
// ============================================================

interface Settings {
  enabled: boolean;
  points_per_currency: number;
  redeem_rate: number;
  welcome_points: number;
}

interface Member {
  userId: number;
  name: string;
  email: string;
  points: number;
  totalSpent: number;
}

interface PointRow {
  id: number;
  nombre: number | null;
  iduser: number | null;
  typepoint: string | null;
  date: string | null;
  raison: string | null;
}

const DEFAULT_SETTINGS: Settings = { enabled: false, points_per_currency: 1, redeem_rate: 100, welcome_points: 0 };

export default function FideliteRecompensesPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [points, setPoints] = useState<PointRow[]>([]);
  const [names, setNames] = useState<Record<number, { name: string; email: string }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Attribution de points à un membre
  const [pointTarget, setPointTarget] = useState<Member | null>(null);
  const [pointAmount, setPointAmount] = useState('');
  const [pointNote, setPointNote] = useState('');
  const [pointBusy, setPointBusy] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const cid = getCommerceId(user);
      const [setRes, pointRes] = await Promise.all([
        supabase.from('loyalty_settings').select('enabled, points_per_currency, redeem_rate, welcome_points').eq('idcommerce', cid).maybeSingle(),
        supabase.from('pointfidelite').select('id, nombre, iduser, typepoint, date, raison').eq('idcommerce', cid).order('date', { ascending: false }).limit(1000),
      ]);
      if (setRes.data) {
        setSettings({
          enabled: !!setRes.data.enabled,
          points_per_currency: Number(setRes.data.points_per_currency ?? 1),
          redeem_rate: Number(setRes.data.redeem_rate ?? 100),
          welcome_points: Number(setRes.data.welcome_points ?? 0),
        });
      }
      const rows = (pointRes.data || []) as PointRow[];
      setPoints(rows);

      const ids = Array.from(new Set(rows.map((r) => r.iduser).filter((x): x is number => typeof x === 'number')));
      if (ids.length > 0) {
        const { data: users } = await supabase.from('users').select('id, name, email').in('id', ids);
        const map: Record<number, { name: string; email: string }> = {};
        (users || []).forEach((u: { id: number; name: string; email: string }) => { map[u.id] = { name: u.name, email: u.email }; });
        setNames(map);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const members = useMemo<Member[]>(() => {
    const map = new Map<number, Member>();
    points.forEach((p) => {
      if (typeof p.iduser !== 'number') return;
      const m = map.get(p.iduser) || { userId: p.iduser, name: names[p.iduser]?.name || `Client #${p.iduser}`, email: names[p.iduser]?.email || '', points: 0, totalSpent: 0 };
      // Le solde agrégé (ou la somme des mouvements) est stocké signé dans `nombre`.
      m.points += Number(p.nombre) || 0;
      map.set(p.iduser, m);
    });
    return Array.from(map.values()).sort((a, b) => b.points - a.points);
  }, [points, names]);

  const totalPoints = members.reduce((s, m) => s + m.points, 0);

  const submitPoints = async () => {
    if (!pointTarget || !user) return;
    const pts = Math.trunc(Number(String(pointAmount).replace(',', '.')));
    if (!Number.isFinite(pts) || pts === 0) {
      setMsg({ type: 'error', text: 'Saisissez un nombre de points (ex. 20 ou -10).' });
      return;
    }
    setPointBusy(true);
    const res = await addLoyaltyPoints({
      merchantId: getCommerceId(user) ?? user.id,
      clientUserId: pointTarget.userId,
      points: pts,
      description: pointNote || undefined,
      actorId: user.id,
      actorName: user.nomcommerce || user.name,
    });
    setPointBusy(false);
    if (!res.success) {
      setMsg({ type: 'error', text: res.error || 'Erreur lors de la mise à jour des points.' });
      return;
    }
    setMsg({ type: 'success', text: `${pts > 0 ? '+' : ''}${pts} points pour ${pointTarget.name}. Nouveau solde : ${res.newTotal ?? 0}.` });
    setPointTarget(null);
    setPointAmount('');
    setPointNote('');
    fetchData();
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setMsg(null);
    try {
      const { error: err } = await supabase.from('loyalty_settings').upsert({
        idcommerce: getCommerceId(user),
        enabled: settings.enabled,
        points_per_currency: settings.points_per_currency,
        redeem_rate: settings.redeem_rate,
        welcome_points: settings.welcome_points,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'idcommerce' });
      if (err) throw err;
      setMsg({ type: 'success', text: 'Paramètres du programme enregistrés.' });
    } catch (e: unknown) {
      setMsg({ type: 'error', text: e instanceof Error ? e.message : 'Erreur lors de l’enregistrement' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950"><i className="ri-vip-crown-line mr-2 text-amber-500"></i>Fidélité & Récompenses</h2>
          <p className="text-sm text-foreground-500 mt-1">Récompensez vos clients fidèles avec un programme de points</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer">
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>} Enregistrer
        </button>
      </div>

      {msg && <div className={`mb-5 px-4 py-3 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-accent-50 text-accent-700' : 'bg-red-50 text-red-600'}`}>{msg.text}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-6">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <i className="ri-user-star-line text-lg text-primary-600"></i>
          <div className="text-2xl font-bold font-heading text-foreground-950 mt-2">{members.length}</div>
          <div className="text-xs text-foreground-500 mt-0.5">Clients membres</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <i className="ri-coin-line text-lg text-amber-500"></i>
          <div className="text-2xl font-bold font-heading text-foreground-950 mt-2">{totalPoints.toLocaleString()}</div>
          <div className="text-xs text-foreground-500 mt-0.5">Points distribués</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 col-span-2 lg:col-span-1">
          <i className="ri-gift-line text-lg text-accent-600"></i>
          <div className="text-2xl font-bold font-heading text-foreground-950 mt-2">{settings.enabled ? 'Actif' : 'Inactif'}</div>
          <div className="text-xs text-foreground-500 mt-0.5">Programme</div>
        </div>
      </div>

      {/* Configuration */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 mb-6 space-y-5">
        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <p className="text-sm font-semibold text-foreground-900">Activer le programme de fidélité</p>
            <p className="text-xs text-foreground-500 mt-0.5">Vos clients accumulent des points à chaque achat</p>
          </div>
          <span className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${settings.enabled ? 'bg-accent-500' : 'bg-background-200'}`}>
            <input type="checkbox" checked={settings.enabled} onChange={(e) => setSettings((s) => ({ ...s, enabled: e.target.checked }))} className="sr-only" />
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${settings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </span>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-background-200/70">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Points par unité dépensée</label>
            <input type="number" min="0" step="0.1" value={settings.points_per_currency}
              onChange={(e) => setSettings((s) => ({ ...s, points_per_currency: parseFloat(e.target.value) || 0 }))}
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Points requis pour 1 unité offerte</label>
            <input type="number" min="1" value={settings.redeem_rate}
              onChange={(e) => setSettings((s) => ({ ...s, redeem_rate: parseInt(e.target.value, 10) || 100 }))}
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Points de bienvenue</label>
            <input type="number" min="0" value={settings.welcome_points}
              onChange={(e) => setSettings((s) => ({ ...s, welcome_points: parseInt(e.target.value, 10) || 0 }))}
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300" />
          </div>
        </div>
        <p className="text-xs text-foreground-400"><i className="ri-information-line mr-1"></i>Exemple : avec 1 point par unité et 100 points requis, 100 unités dépensées offrent 1 unité de réduction.</p>
      </div>

      {/* Membres */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-5 py-3 bg-background-100 border-b border-background-200/70">
          <h3 className="text-sm font-semibold text-foreground-700 flex items-center gap-2"><i className="ri-group-line text-primary-500"></i>Clients membres</h3>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : error ? (
          <div className="flex flex-col items-center py-12"><p className="text-sm text-red-500 mb-3">{error}</p><button onClick={fetchData} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm cursor-pointer">Réessayer</button></div>
        ) : members.length === 0 ? (
          <div className="text-center py-14"><i className="ri-user-star-line text-3xl text-foreground-300 mb-2 block"></i><p className="text-sm text-foreground-500">Aucun point de fidélité enregistré pour le moment.</p></div>
        ) : (
          <div className="divide-y divide-background-200/70">
            {members.slice(0, 15).map((m, i) => (
              <div key={m.userId} className="flex items-center gap-4 px-5 py-3.5 hover:bg-background-50/50">
                <span className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700 flex-shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground-900 truncate">{m.name}</p>
                  {m.email && <p className="text-xs text-foreground-400 truncate">{m.email}</p>}
                </div>
                <span className="text-sm font-bold text-amber-600 whitespace-nowrap flex items-center gap-1"><i className="ri-coin-line"></i>{m.points.toLocaleString()} pts</span>
                <button
                  onClick={() => { setPointTarget(m); setPointAmount(''); setPointNote(''); setMsg(null); }}
                  title="Ajouter / retirer des points"
                  className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center hover:bg-amber-200 transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-add-line text-sm"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modale : ajouter / retirer des points */}
      {pointTarget && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !pointBusy && setPointTarget(null)} />
          <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
            <div className="w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70">
              <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-star-line text-amber-500"></i>Points de fidélité · {pointTarget.name}
                </h3>
                <button onClick={() => !pointBusy && setPointTarget(null)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-foreground-500"></i>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-xs text-foreground-500 leading-relaxed flex items-start gap-2">
                  <i className="ri-information-line text-amber-500 mt-0.5"></i>
                  <span>Solde actuel : <strong>{pointTarget.points.toLocaleString()} pts</strong>. Saisissez un nombre positif pour ajouter, négatif pour retirer.</span>
                </p>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nombre de points (+ ou -)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    value={pointAmount}
                    onChange={(e) => setPointAmount(e.target.value.replace(/[^\d-]/g, ''))}
                    placeholder="20"
                    className="w-full h-12 px-4 rounded-lg border border-background-200/70 bg-background-50 text-lg font-semibold text-foreground-950 focus:outline-none focus:border-amber-300 focus:ring-1 focus:ring-amber-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Motif (optionnel)</label>
                  <input
                    type="text"
                    value={pointNote}
                    onChange={(e) => setPointNote(e.target.value)}
                    maxLength={120}
                    placeholder="Ex. Achat de 250 MAD"
                    className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-amber-300 focus:ring-1 focus:ring-amber-300"
                  />
                </div>
              </div>
              <div className="p-4 border-t border-background-200/70 flex gap-2">
                <button onClick={() => !pointBusy && setPointTarget(null)} className="flex-1 h-11 rounded-full bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer">Annuler</button>
                <button onClick={submitPoints} disabled={pointBusy} className="flex-1 h-11 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2">
                  {pointBusy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                  Valider
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}